import { useMemo, useState } from 'react';
import { Modal, ModalBody } from 'reactstrap';
import useKioskOption from '@lib/hooks/useKioskOption';
import { getDeviceLocationText } from '@util/text';
import styled from 'styled-components';

const ModalBackground = styled.div`
  background-color: rgba(0, 0, 0, 0.5);
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  z-index: 101;
`;

const Select = styled.select`
  background: #f8fcff;
  border: 1px solid #e9e9e9;
  border-radius: 4px;
  padding: 8px 15px;
  margin-right: 20px;
  width: 210px;

  > p {
    font-weight: 500;
  }
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;

  .btn-primary {
    margin-left: 10px;
  }
`;

interface OpenDoorModalProps {
  show: boolean;
  onClose: () => void;
  kind: 'PC' | 'Program' | 'Door' | 'Update';
}

const SocketControlModal = ({ show, onClose, kind }: OpenDoorModalProps) => {
  const {
    kioskOptions,
    usingLegacyMultiKiosk,
    usingMultiKiosk,
    legacyMultiKioskFloor,
    handleOpenDoor,
    handleOpenDoorLegacy,
    handleRestartProgramLegacy,
    handleRestartPCLegacy,
    handleRestartProgram,
    handleRestartPC,
    handleUpdate,
  } = useKioskOption();
  const [choiceNum, setChoiceNum] = useState(1);
  const [selectedMulti, setSelectedMulti] = useState<{
    number: number;
    location: DeviceLocation;
  } | null>(null);

  const openDoor = () => {
    if (usingLegacyMultiKiosk) return handleOpenDoorLegacy(choiceNum);
    if (!selectedMulti) return;

    return handleOpenDoor(selectedMulti.number, selectedMulti.location);
  };

  const restartPC = () => {
    if (usingLegacyMultiKiosk) return handleRestartPCLegacy(choiceNum);
    if (!selectedMulti) return;

    return handleRestartPC(selectedMulti.number, selectedMulti.location);
  };

  const restartProgram = () => {
    if (usingLegacyMultiKiosk) return handleRestartProgramLegacy(choiceNum);
    if (!selectedMulti) return;

    return handleRestartProgram(selectedMulti.number, selectedMulti.location);
  };

  const updateProgram = () => {
    // 레거시는 kiosk_setting.jsx에서 직접 외부/내부 버튼으로 처리
    // 이 모달은 신규 멀티키오스크에서만 사용
    if (!selectedMulti) return;
    return handleUpdate(selectedMulti.number, selectedMulti.location);
  };

  const onClickConfirm = () => {
    if (kind === 'Door') return openDoor();
    if (kind === 'PC') return restartPC();
    if (kind === 'Program') return restartProgram();
    if (kind === 'Update') return updateProgram();
  };

  const KindText = useMemo(() => {
    if (kind === 'Door') return '문을 열 키오스크를 선택해 주세요.';
    if (kind === 'PC') return `재시작할 ${usingLegacyMultiKiosk ? '내부' : ''} PC를 선택해 주세요.`;
    if (kind === 'Program')
      return `재시작할 ${usingLegacyMultiKiosk ? '내부' : ''} 프로그램을 선택해 주세요.`;
    if (kind === 'Update') return '업데이트할 키오스크를 선택해 주세요.';
    return '-';
  }, [kind, usingLegacyMultiKiosk]);

  const ButtonText = useMemo(() => {
    if (kind === 'Door') return '문 열기';
    if (kind === 'PC') return `PC 재시작`;
    if (kind === 'Program') return `프로그램 재시작`;
    if (kind === 'Update') return '업데이트';
    return '-';
  }, [kind]);

  const FloorSelected = useMemo(() => {
    if (usingLegacyMultiKiosk) return true;
    return !!selectedMulti;
  }, [usingLegacyMultiKiosk, selectedMulti]);

  return (
    <>
      <Modal isOpen={show} toggle={onClose} centered>
        <ModalBody>
          <p>{KindText}</p>
          {usingLegacyMultiKiosk && (
            <Select value={choiceNum} onChange={(e) => setChoiceNum(+e.target.value)}>
              {legacyMultiKioskFloor &&
                [...Array(legacyMultiKioskFloor)].map((item, index) => (
                  <option value={index + 1}>{index + 1}층</option>
                ))}
            </Select>
          )}
          {usingMultiKiosk && !usingLegacyMultiKiosk && (
            <Select
              value={selectedMulti ? `${selectedMulti.location}-${selectedMulti.number}` : '0'}
              onChange={(e) => {
                if (e.target.value === '0') {
                  setSelectedMulti(null);
                  return;
                }
                const found = kioskOptions.find(
                  (opt) => `${opt.location}-${opt.number}` === e.target.value,
                );
                if (found) {
                  setSelectedMulti({ number: found.number, location: found.location });
                }
              }}>
              <option value="0">선택하세요</option>
              {usingMultiKiosk &&
                kioskOptions.map((item) => (
                  <option
                    key={`${item.location}-${item.number}`}
                    value={`${item.location}-${item.number}`}>
                    {getDeviceLocationText(item.location)} - {item.number}
                  </option>
                ))}
            </Select>
          )}

          <Buttons>
            <button className="btn-secondary btn-sm" onClick={() => onClose()}>
              취소
            </button>
            {/* {kind === 'PC' ? (
              <button className="btn-primary btn-sm" onClick={() => multiPcRestart(id, +choiceNum)}>
                PC재시작
              </button>
            ) : kind === 'Program' ? (
              <button
                className="btn-primary btn-sm"
                onClick={() => multiProgramRestart(id, +choiceNum)}>
                프로그램 재시작
              </button>
            ) : ( */}
            <button
              disabled={!FloorSelected}
              className="btn-primary btn-sm"
              onClick={onClickConfirm}>
              {ButtonText}
            </button>
            {/* )} */}
          </Buttons>
        </ModalBody>
      </Modal>
      <ModalBackground />
    </>
  );
};

export default SocketControlModal;
